/**
 * @besper/fe-shared – ToastContainer
 * Context provider for toast notifications; styling via config theme (Toast.css).
 */
import React, { useState, useCallback } from 'react';
import Toast from '../Toast';

const MAX_TOASTS = 5;

export const ToastContext = React.createContext({
  showToast: () => {},
  showSuccess: () => {},
  showError: () => {},
  showWarning: () => {},
  showInfo: () => {},
  hideToast: () => {},
  clearAll: () => {},
});

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastContainer');
  }
  return context;
};

const ToastContainer = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((toastData) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      variant: 'dark',
      position: 'top-right',
      ...toastData,
      show: true,
    };

    setToasts((prev) => {
      const newToasts = [...prev, toast];
      if (newToasts.length > MAX_TOASTS) {
        return newToasts.slice(-MAX_TOASTS);
      }
      return newToasts;
    });
    return id;
  }, []);

  const hideToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showSuccess = useCallback((title, message, options = {}) => {
    if (typeof title === 'string' && message === undefined) {
      return showToast({ type: 'success', message: title, duration: 5000, variant: 'dark', ...options });
    }
    return showToast({ type: 'success', title, message, duration: 5000, variant: 'dark', ...options });
  }, [showToast]);

  const showError = useCallback((title, message, options = {}) => {
    if (typeof title === 'string' && message === undefined) {
      return showToast({ type: 'error', message: title, duration: 7000, variant: 'dark', ...options });
    }
    return showToast({ type: 'error', title, message, duration: 7000, variant: 'dark', ...options });
  }, [showToast]);

  const showWarning = useCallback((title, message, options = {}) => {
    if (typeof title === 'string' && message === undefined) {
      return showToast({ type: 'warning', message: title, duration: 6000, variant: 'dark', ...options });
    }
    return showToast({ type: 'warning', title, message, duration: 6000, variant: 'dark', ...options });
  }, [showToast]);

  const showInfo = useCallback((title, message, options = {}) => {
    if (typeof title === 'string' && message === undefined) {
      return showToast({ type: 'info', message: title, duration: 4000, variant: 'dark', ...options });
    }
    return showToast({ type: 'info', title, message, duration: 4000, variant: 'dark', ...options });
  }, [showToast]);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideToast,
    clearAll,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="toast-container">
        {toasts.map((toast, index) => {
          const stackOffset = index * 12;
          return (
            <Toast
              key={toast.id}
              {...toast}
              onClose={hideToast}
              style={{
                zIndex: 99999 + index,
                transform:
                  toast.position === 'top-right' || toast.position === 'bottom-right'
                    ? `translateY(${stackOffset}px)`
                    : toast.position === 'top-left' || toast.position === 'bottom-left'
                    ? `translateY(${stackOffset}px)`
                    : undefined,
              }}
            />
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastContainer;
